import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { stylingAnalysisApi } from '../lib/styling-analysis-api.js';
import { queryKeys } from '../lib/query-keys.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business Fetch daily per-stylist styling stats for the given date range.
 * @graph-connects /styling-analysis/daily-stylist-stats [calls] BQ aggregate endpoint
 */
export function useDailyStylistStats(from?: string, to?: string) {
  return useQuery({
    queryKey: queryKeys.stylingAnalysis.dailyStylistStats(from, to),
    queryFn: () => stylingAnalysisApi.getDailyStylistStats({ from, to }),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business Fetch request trends (scene distribution and monthly aggregation).
 * @graph-connects /styling-analysis/request-trends [calls] BQ request trends endpoint
 */
export function useRequestTrends(from?: string, to?: string) {
  return useQuery({
    queryKey: queryKeys.stylingAnalysis.requestTrends(from, to),
    queryFn: () => stylingAnalysisApi.getRequestTrends({ from, to }),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business Fetch paginated raw customer request list.
 * @graph-connects /styling-analysis/requests [calls] BQ request list endpoint
 */
export function useRequestList(from?: string, to?: string, limit?: number, offset?: number) {
  return useQuery({
    queryKey: queryKeys.stylingAnalysis.requests(from, to, limit, offset),
    queryFn: () => stylingAnalysisApi.getRequests({ from, to, limit, offset }),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business ドリルダウン表示用の個別スタイリング詳細レコードを取得する。
 * @graph-connects /styling-analysis/daily-details [calls] BQ detail drilldown endpoint
 */
export function useStylingDetails(
  date: string | null,
  type: 'completed' | 'rejected' | 'error' | null,
  stylistId?: string,
) {
  const queryDate = date ?? '';
  const queryType = type ?? 'completed';
  return useQuery({
    queryKey: queryKeys.stylingAnalysis.dailyDetails(date, type, stylistId),
    queryFn: () =>
      stylingAnalysisApi.getDailyDetails({
        date: queryDate,
        type: queryType,
        stylistId,
      }),
    staleTime: 5 * 60_000,
    retry: false,
    enabled: !!date && !!type,
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 最新の保存済みスタイリング AI 分析結果を取得する。
 * @graph-connects stylingAnalysisApi.getAiTrendsLatest [calls] API クライアント
 */
export function useAiTrendsLatest(enabled?: boolean) {
  return useQuery({
    queryKey: queryKeys.stylingAnalysis.aiTrendsLatest,
    queryFn: () => stylingAnalysisApi.getAiTrendsLatest(),
    enabled: enabled ?? true,
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business スタイリング AI 分析の履歴一覧を取得する。
 * @graph-connects stylingAnalysisApi.getAiTrendsHistory [calls] API クライアント
 */
export function useAiTrendsHistory(enabled?: boolean) {
  return useQuery({
    queryKey: queryKeys.stylingAnalysis.aiTrendsHistory,
    queryFn: () => stylingAnalysisApi.getAiTrendsHistory(),
    enabled: enabled ?? true,
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business スタイリング AI 分析を実行し結果を保存するミューテーション。
 * @graph-connects stylingAnalysisApi.runAiTrends [calls] API クライアント
 */
export function useRunAiTrends() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { from?: string; to?: string }) => stylingAnalysisApi.runAiTrends(params),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.stylingAnalysis.aiTrendsLatest });
      void queryClient.invalidateQueries({ queryKey: queryKeys.stylingAnalysis.aiTrendsHistory });
    },
  });
}

/**
 * @graph-business 日別選定パフォーマンス（件数・平均時間・稼働人数）を取得する。
 * @graph-connects /styling-analysis/daily-performance [calls] BQ daily performance endpoint
 */
export function useDailyPerformance(from?: string, to?: string, enabled?: boolean) {
  return useQuery({
    queryKey: queryKeys.stylingAnalysis.dailyPerformance(from, to),
    queryFn: () => stylingAnalysisApi.getDailyPerformance({ from, to }),
    staleTime: 5 * 60_000,
    retry: false,
    enabled: enabled ?? true,
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business レーティング分析（日次データ、選定日/返却日起算切替対応）を取得する。
 * @graph-connects /styling-analysis/rating-analysis [calls] BQ rating analysis endpoint
 */
export function useRatingAnalysis(
  from?: string,
  to?: string,
  dateBase?: string,
  enabled?: boolean,
) {
  return useQuery({
    queryKey: queryKeys.stylingAnalysis.ratingAnalysis(from, to, dateBase),
    queryFn: () => stylingAnalysisApi.getRatingAnalysis({ from, to, dateBase }),
    staleTime: 5 * 60_000,
    retry: false,
    enabled: enabled ?? true,
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business リクエストインサイト（セグメント別記入率・文字数×再選定率・月別セグメント推移・シーンクロス集計）を取得する。
 * @graph-connects /styling-analysis/request-insights [calls] BQ request insights endpoint
 */
export function useRequestInsights(from?: string, to?: string, enabled?: boolean) {
  return useQuery({
    queryKey: queryKeys.stylingAnalysis.requestInsights(from, to),
    queryFn: () => stylingAnalysisApi.getRequestInsights({ from, to }),
    staleTime: 5 * 60_000,
    retry: false,
    enabled: enabled ?? true,
  });
}
